import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Format date to relative time (e.g., "2 hours ago", "3 days ago")
export function formatDistanceToNow(date) {
    const now = new Date();
    const diff = now - date;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    
    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 4) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return date.toLocaleDateString();
}

// Format duration in seconds to MM:SS
export function formatDuration(seconds) {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Format date for display
export function formatDate(date) {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

// Musical notes with frequencies
export const NOTES = [
    { name: 'C', frequency: 261.63 },
    { name: 'C#/Db', frequency: 277.18 },
    { name: 'D', frequency: 293.66 },
    { name: 'D#/Eb', frequency: 311.13 },
    { name: 'E', frequency: 329.63 },
    { name: 'F', frequency: 349.23 },
    { name: 'F#/Gb', frequency: 369.99 },
    { name: 'G', frequency: 392.00 },
    { name: 'G#/Ab', frequency: 415.30 },
    { name: 'A', frequency: 440.00 },
    { name: 'A#/Bb', frequency: 466.16 },
    { name: 'B', frequency: 493.88 }
];

// Guitar string frequencies (standard tuning)
export const GUITAR_STRINGS = [
    { string: 6, note: 'E2', frequency: 82.41, label: 'Low E' },
    { string: 5, note: 'A2', frequency: 110.00, label: 'A' },
    { string: 4, note: 'D3', frequency: 146.83, label: 'D' },
    { string: 3, note: 'G3', frequency: 196.00, label: 'G' },
    { string: 2, note: 'B3', frequency: 246.94, label: 'B' },
    { string: 1, note: 'E4', frequency: 329.63, label: 'High E' }
];

// Tuning presets with string frequencies
export const TUNING_PRESETS = {
    'Standard': [
        { string: 6, note: 'E2', frequency: 82.41, label: 'Low E' },
        { string: 5, note: 'A2', frequency: 110.00, label: 'A' },
        { string: 4, note: 'D3', frequency: 146.83, label: 'D' },
        { string: 3, note: 'G3', frequency: 196.00, label: 'G' },
        { string: 2, note: 'B3', frequency: 246.94, label: 'B' },
        { string: 1, note: 'E4', frequency: 329.63, label: 'High E' }
    ],
    'Drop D': [
        { string: 6, note: 'D2', frequency: 73.42, label: 'Low D' },
        { string: 5, note: 'A2', frequency: 110.00, label: 'A' },
        { string: 4, note: 'D3', frequency: 146.83, label: 'D' },
        { string: 3, note: 'G3', frequency: 196.00, label: 'G' },
        { string: 2, note: 'B3', frequency: 246.94, label: 'B' },
        { string: 1, note: 'E4', frequency: 329.63, label: 'High E' }
    ],
    'Drop C': [
        { string: 6, note: 'C2', frequency: 65.41, label: 'Low C' },
        { string: 5, note: 'G2', frequency: 98.00, label: 'G' },
        { string: 4, note: 'C3', frequency: 130.81, label: 'C' },
        { string: 3, note: 'F3', frequency: 174.61, label: 'F' },
        { string: 2, note: 'A3', frequency: 220.00, label: 'A' },
        { string: 1, note: 'D4', frequency: 293.66, label: 'High D' }
    ],
    'Open G': [
        { string: 6, note: 'D2', frequency: 73.42, label: 'Low D' },
        { string: 5, note: 'G2', frequency: 98.00, label: 'G' },
        { string: 4, note: 'D3', frequency: 146.83, label: 'D' },
        { string: 3, note: 'G3', frequency: 196.00, label: 'G' },
        { string: 2, note: 'B3', frequency: 246.94, label: 'B' },
        { string: 1, note: 'D4', frequency: 293.66, label: 'High D' }
    ],
    'Open D': [
        { string: 6, note: 'D2', frequency: 73.42, label: 'Low D' },
        { string: 5, note: 'A2', frequency: 110.00, label: 'A' },
        { string: 4, note: 'D3', frequency: 146.83, label: 'D' },
        { string: 3, note: 'F#3', frequency: 185.00, label: 'F#' },
        { string: 2, note: 'A3', frequency: 220.00, label: 'A' },
        { string: 1, note: 'D4', frequency: 293.66, label: 'High D' }
    ],
    'Open Dm': [
        { string: 6, note: 'D2', frequency: 73.42, label: 'Low D' },
        { string: 5, note: 'A2', frequency: 110.00, label: 'A' },
        { string: 4, note: 'D3', frequency: 146.83, label: 'D' },
        { string: 3, note: 'F3', frequency: 174.61, label: 'F' },
        { string: 2, note: 'A3', frequency: 220.00, label: 'A' },
        { string: 1, note: 'D4', frequency: 293.66, label: 'High D' }
    ],
    'DADGAD': [
        { string: 6, note: 'D2', frequency: 73.42, label: 'Low D' },
        { string: 5, note: 'A2', frequency: 110.00, label: 'A' },
        { string: 4, note: 'D3', frequency: 146.83, label: 'D' },
        { string: 3, note: 'G3', frequency: 196.00, label: 'G' },
        { string: 2, note: 'A3', frequency: 220.00, label: 'A' },
        { string: 1, note: 'D4', frequency: 293.66, label: 'High D' }
    ],
    'DAEAC#E': [
        { string: 6, note: 'D2', frequency: 73.42, label: 'Low D' },
        { string: 5, note: 'A2', frequency: 110.00, label: 'A' },
        { string: 4, note: 'E3', frequency: 164.81, label: 'E' },
        { string: 3, note: 'A3', frequency: 220.00, label: 'A' },
        { string: 2, note: 'C#4', frequency: 277.18, label: 'C#' },
        { string: 1, note: 'E4', frequency: 329.63, label: 'High E' }
    ],
    'FACGCE': [
        { string: 6, note: 'F2', frequency: 87.31, label: 'Low F' },
        { string: 5, note: 'A2', frequency: 110.00, label: 'A' },
        { string: 4, note: 'C3', frequency: 130.81, label: 'C' },
        { string: 3, note: 'G3', frequency: 196.00, label: 'G' },
        { string: 2, note: 'C4', frequency: 261.63, label: 'C' },
        { string: 1, note: 'E4', frequency: 329.63, label: 'High E' }
    ],
    'Half Step Down': [
        { string: 6, note: 'Eb2', frequency: 77.78, label: 'Low Eb' },
        { string: 5, note: 'Ab2', frequency: 103.83, label: 'Ab' },
        { string: 4, note: 'Db3', frequency: 138.59, label: 'Db' },
        { string: 3, note: 'Gb3', frequency: 185.00, label: 'Gb' },
        { string: 2, note: 'Bb3', frequency: 233.08, label: 'Bb' },
        { string: 1, note: 'Eb4', frequency: 311.13, label: 'High Eb' }
    ]
};

// Common guitar tunings
export const GUITAR_TUNINGS = [
    { name: 'Standard', notes: 'E A D G B E' },
    { name: 'Drop D', notes: 'D A D G B E' },
    { name: 'Half Step Down', notes: 'Eb Ab Db Gb Bb Eb' },
    { name: 'Open G', notes: 'D G D G B D' },
    { name: 'Open D', notes: 'D A D F# A D' },
    { name: 'DADGAD', notes: 'D A D G A D' },
    { name: 'Open E', notes: 'E B E G# B E' }
];

// Common keys
export const MUSICAL_KEYS = [
    'C Major', 'C Minor',
    'D Major', 'D Minor',
    'E Major', 'E Minor',
    'F Major', 'F Minor',
    'G Major', 'G Minor',
    'A Major', 'A Minor',
    'B Major', 'B Minor',
    'Db Major', 'Eb Minor',
    'Ab Major', 'Bb Minor'
];

// Common feels/moods
export const SONG_FEELS = [
    'Upbeat', 'Melancholic', 'Dreamy', 'Energetic',
    'Peaceful', 'Dark', 'Hopeful', 'Nostalgic',
    'Intimate', 'Epic', 'Playful', 'Intense'
];

// Custom fragment color options (calm, muted palette)
export const FRAGMENT_COLORS = [
    { id: 'default', label: 'Default', cssVar: null },
    { id: 'taupe', label: 'Taupe', cssVar: '--custom-color-1' },
    { id: 'sage', label: 'Sage', cssVar: '--custom-color-2' },
    { id: 'sky', label: 'Sky', cssVar: '--custom-color-3' },
    { id: 'lavender', label: 'Lavender', cssVar: '--custom-color-4' },
    { id: 'rose', label: 'Rose', cssVar: '--custom-color-5' },
    { id: 'sand', label: 'Sand', cssVar: '--custom-color-6' },
    { id: 'seafoam', label: 'Seafoam', cssVar: '--custom-color-7' },
    { id: 'honey', label: 'Honey', cssVar: '--custom-color-8' }
];

// Pitch detection helper - find closest note
export function findClosestNote(frequency) {
    if (!frequency || frequency < 60 || frequency > 1000) return null;
    
    // Extended note frequencies across octaves
    const allNotes = [];
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    
    for (let octave = 1; octave <= 6; octave++) {
        for (let i = 0; i < 12; i++) {
            const freq = 440 * Math.pow(2, (i - 9 + (octave - 4) * 12) / 12);
            allNotes.push({
                name: noteNames[i],
                octave,
                frequency: freq,
                fullName: `${noteNames[i]}${octave}`
            });
        }
    }
    
    let closest = allNotes[0];
    let minDiff = Math.abs(frequency - closest.frequency);
    
    for (const note of allNotes) {
        const diff = Math.abs(frequency - note.frequency);
        if (diff < minDiff) {
            minDiff = diff;
            closest = note;
        }
    }
    
    // Calculate cents deviation
    const cents = 1200 * Math.log2(frequency / closest.frequency);
    
    return {
        ...closest,
        detectedFrequency: frequency,
        cents: Math.round(cents),
        inTune: Math.abs(cents) < 10
    };
}